import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { MeetingListItem } from '@ai-meeting/shared'
import SelectField from '@/components/SelectField'
import SidebarToggle from '@/components/SidebarToggle'
import DateInput from '@/components/DateInput'
import PaginationButton from '@/components/PaginationButton'
import { SearchIcon, TrashIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { formatDateTime } from '@/utils/formatDateTime'
import {
  deleteMeeting,
  deleteMeetingPermanently,
  deleteMeetingsPermanently,
  fetchDeletedMeetingList,
  fetchMeetingList,
  hideMeetingFromHistory,
  restoreMeeting,
} from '@/services/meetings'
import DeleteMeetingModal from './components/DeleteMeetingModal'
import RemoveMeetingModal from './components/RemoveMeetingModal'
import ShareMeetingFlow from './components/ShareMeetingFlow'
import { useStore } from '@/store/useStore.ts'
import { readOwnedMeetingCount } from '@/utils/resolveDefaultMeetingName'

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 300
const TRASH_RETENTION_DAYS = 30
const MAX_PAGE_BUTTONS = 5

type DirectionFilter = 'all' | 'to-ja' | 'to-en' | 'to-vi'
type HistoryTab = 'active' | 'deleted'

/**
 * Builds up to `maxButtons` consecutive page numbers centered on `current`,
 * clamped to `[1, total]` — without this, a fixed `[1..maxButtons]` window
 * makes any page past `maxButtons` unreachable via the numbered buttons
 * (BRAI-154).
 */
function getPageButtonWindow(current: number, total: number, maxButtons: number): number[] {
  if (total <= maxButtons) return Array.from({ length: total }, (_, i) => i + 1)

  let start = Math.max(1, current - Math.floor(maxButtons / 2))
  const end = Math.min(total, start + maxButtons - 1)
  start = Math.max(1, end - maxButtons + 1)

  return Array.from({ length: maxButtons }, (_, i) => start + i)
}

function getLanguageLabel(language: string): string {
  const normalized = language.toLowerCase()
  if (!normalized || normalized === 'detect' || normalized === 'auto') return 'AUTO'
  return normalized.slice(0, 2).toUpperCase()
}

function formatDuration(minutes: number, t: ReturnType<typeof useUiStrings>['t']): string {
  if (minutes <= 0) return '—'
  return `${minutes} ${t.min}`
}

function formatDeletedRemainingDays(
  deletedAt: number | null | undefined,
  t: ReturnType<typeof useUiStrings>['t']
): string {
  if (!deletedAt) return fmt(t.remainingDays, { count: 0 })

  const elapsedDays = Math.floor((Date.now() - deletedAt) / 86_400_000)
  const remainingDays = Math.max(0, TRASH_RETENTION_DAYS - elapsedDays)
  return fmt(t.remainingDays, { count: remainingDays })
}

function parseDirectionFilter(filter: DirectionFilter): {
  targetLang?: string
} {
  if (filter === 'all') return {}
  return { targetLang: filter.replace('to-', '') }
}

function MeetingGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 2h6l4 4v16H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v5h5M9 13h6M9 17h6" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
      />
      <circle cx="9" cy="7" r="4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

export default function HistoryPage() {
  const { t, locale } = useUiStrings()
  const savedMeetingCount = useStore((s) => s.savedMeetingCount)
  const setSavedMeetingCount = useStore((s) => s.setSavedMeetingCount)
  const [meetings, setMeetings] = useState<MeetingListItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [direction, setDirection] = useState<DirectionFilter>('all')
  const [dateFilter, setDateFilter] = useState('')
  const [page, setPage] = useState(1)
  const [tab, setTab] = useState<HistoryTab>('active')
  const [meetingToDelete, setMeetingToDelete] = useState<MeetingListItem | null>(null)
  const [meetingToShare, setMeetingToShare] = useState<MeetingListItem | null>(null)
  const [meetingToRemove, setMeetingToRemove] = useState<MeetingListItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [selectedActiveIds, setSelectedActiveIds] = useState<string[]>([])
  const [selectedDeletedIds, setSelectedDeletedIds] = useState<string[]>([])
  const [historyActionLoading, setHistoryActionLoading] = useState(false)
  const [trashActionLoading, setTrashActionLoading] = useState(false)
  const [confirmBulkActiveDelete, setConfirmBulkActiveDelete] = useState(false)
  const [meetingToPermanentlyDelete, setMeetingToPermanentlyDelete] =
    useState<MeetingListItem | null>(null)
  const [confirmBulkPermanentDelete, setConfirmBulkPermanentDelete] = useState(false)

  // Bumped after every delete/restore action to force the list effect below
  // to refetch even when `page` is already 1 (setPage(1) alone would be a
  // no-op dependency change in that case). Delete/restore handlers no longer
  // guess the new total/page locally — they always defer to a fresh
  // server fetch instead.
  const [reloadToken, setReloadToken] = useState(0)

  /** Sends the user back to page 1 and forces a refetch of the current tab's list. */
  function refetchFromPageOne(): void {
    setPage(1)
    setReloadToken((token) => token + 1)
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query])

  useEffect(() => {
    setPage(1)
    setSelectedActiveIds([])
    setSelectedDeletedIds([])
  }, [debouncedQuery, direction, dateFilter, tab])

  useEffect(() => {
    let ignore = false
    const { targetLang } = parseDirectionFilter(direction)

    let dateFrom: number | undefined
    let dateTo: number | undefined
    if (dateFilter) {
      const start = new Date(`${dateFilter}T00:00:00`)
      const end = new Date(`${dateFilter}T23:59:59.999`)
      dateFrom = start.getTime()
      dateTo = end.getTime()
    }

    async function loadMeetings() {
      try {
        setLoading(true)
        setError(null)
        const listQuery = {
          search: debouncedQuery.trim() || undefined,
          targetLang,
          // When the meeting happened, not when the row was last written.
          // `updatedAt` is Prisma's `@updatedAt`, which every background sweep
          // bumps — and they all run on boot, so a deploy used to drag whole
          // batches of old meetings to the top of somebody's history.
          sortBy: 'createdAt' as const,
          sortOrder: 'desc' as const,
          dateFrom,
          dateTo,
          page,
          limit: PAGE_SIZE,
        }

        const data =
          tab === 'deleted'
            ? await fetchDeletedMeetingList(listQuery)
            : await fetchMeetingList({
                ...listQuery,
                status: 'recent',
              })
        if (!ignore) {
          setMeetings(data.items)
          setTotal(data.total)
          setTotalPages(data.totalPages)
          if (tab === 'active') setSavedMeetingCount(readOwnedMeetingCount(data, savedMeetingCount))
        }
      } catch (err) {
        if (!ignore) setError(err instanceof Error ? err.message : t.failedLoadMeetings)
      } finally {
        if (!ignore) setLoading(false)
      }
    }

    void loadMeetings()
    return () => {
      ignore = true
    }
  }, [debouncedQuery, direction, dateFilter, page, tab, reloadToken])

  async function handleConfirmDelete() {
    if (!meetingToDelete) return

    try {
      setDeleting(true)
      await deleteMeeting(meetingToDelete.id)
      setMeetingToDelete(null)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedDeleteMeeting)
    } finally {
      setDeleting(false)
    }
  }

  function toggleActiveSelection(id: string) {
    setSelectedActiveIds((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
    )
  }

  function toggleAllActiveSelection(checked: boolean) {
    const ownedIds = meetings
      .filter((meeting) => (meeting.accessState ?? meeting.ownership) === 'owner' || meeting.ownership === 'owned')
      .map((meeting) => meeting.id)
    setSelectedActiveIds(checked ? ownedIds : [])
  }

  function toggleDeletedSelection(id: string) {
    setSelectedDeletedIds((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
    )
  }

  async function handleDeleteSelectedActiveMeetings() {
    if (selectedActiveIds.length === 0) return

    try {
      setHistoryActionLoading(true)
      await Promise.all(selectedActiveIds.map((id) => deleteMeeting(id)))
      setSelectedActiveIds([])
      setConfirmBulkActiveDelete(false)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedDeleteMeeting)
    } finally {
      setHistoryActionLoading(false)
    }
  }

  function toggleAllDeletedSelection(checked: boolean) {
    setSelectedDeletedIds(checked ? meetings.map((meeting) => meeting.id) : [])
  }

  async function handleRestoreMeeting(meeting: MeetingListItem) {
    try {
      setTrashActionLoading(true)
      await restoreMeeting(meeting.id)
      setSelectedDeletedIds((current) => current.filter((id) => id !== meeting.id))
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedRestoreMeeting)
    } finally {
      setTrashActionLoading(false)
    }
  }

  async function handlePermanentDeleteMeeting(meeting: MeetingListItem) {
    try {
      setTrashActionLoading(true)
      await deleteMeetingPermanently(meeting.id)
      setSelectedDeletedIds((current) => current.filter((id) => id !== meeting.id))
      setMeetingToPermanentlyDelete(null)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedPermanentDeleteMeeting)
    } finally {
      setTrashActionLoading(false)
    }
  }

  async function handlePermanentDeleteSelected() {
    if (selectedDeletedIds.length === 0) return

    try {
      setTrashActionLoading(true)
      await deleteMeetingsPermanently(selectedDeletedIds)
      setSelectedDeletedIds([])
      setConfirmBulkPermanentDelete(false)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedPermanentDeleteMeetings)
    } finally {
      setTrashActionLoading(false)
    }
  }

  async function handleRemoveFromHistory() {
    if (!meetingToRemove) return
    try {
      setRemoving(true)
      await hideMeetingFromHistory(meetingToRemove.id)
      setMeetingToRemove(null)
      refetchFromPageOne()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedHideMeeting)
    } finally {
      setRemoving(false)
    }
  }

  const currentPage = Math.min(page, totalPages)
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total)
  const ownedVisibleMeetings = meetings.filter(
    (meeting) =>
      meeting.accessState === 'owner' ||
      (meeting.accessState == null && meeting.ownership !== 'shared'),
  )
  const allActiveSelected =
    tab === 'active' &&
    ownedVisibleMeetings.length > 0 &&
    ownedVisibleMeetings.every((meeting) => selectedActiveIds.includes(meeting.id))
  const allDeletedSelected =
    tab === 'deleted' && meetings.length > 0 && selectedDeletedIds.length === meetings.length

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 sm:px-6">
        <SidebarToggle />
        <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-[var(--text-primary)]">
          {t.meetingHistory}
        </h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]">
          {tab === 'active' ? t.historyBreadcrumb : t.trash}
        </p>
        <h2 className="mt-2 text-lg font-bold text-[var(--text-primary)]">
          {tab === 'active' ? t.meetingHistory : t.meetingTrash}
        </h2>
        <p className="mt-1.5 text-sm text-[var(--text-secondary)]">
          {tab === 'active' ? t.historySubtitle : t.trashSubtitle}
        </p>

        <div className="mt-5 inline-flex rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-1">
          <button
            type="button"
            onClick={() => setTab('active')}
            className={[
              'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
              tab === 'active'
                ? 'bg-[var(--accent)] text-white'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]',
            ].join(' ')}
          >
            {t.activeTab}
          </button>
          <button
            type="button"
            onClick={() => setTab('deleted')}
            className={[
              'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
              tab === 'deleted'
                ? 'bg-[var(--accent)] text-white'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]',
            ].join(' ')}
          >
            {t.deletedTab}
          </button>
        </div>

        <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_200px_200px]">
          <label className="relative block">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.searchPlaceholder}
              className="input-field pl-10"
            />
          </label>

          <DateInput
            value={dateFilter}
            onChange={setDateFilter}
            ariaLabel={t.filterByDate}
            placeholder={t.datePlaceholder}
          />

          <SelectField
            label={t.filterByDirection}
            labelHidden
            value={direction}
            onChange={(value) => setDirection(value as DirectionFilter)}
          >
            <option value="all">{t.allDirections}</option>
            <option value="to-ja">{t.directionToJapanese}</option>
            <option value="to-en">{t.directionToEnglish}</option>
            <option value="to-vi">{t.directionToVietnamese}</option>
          </SelectField>
        </div>

        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--accent)]"
        >
          <FilterIcon className="h-3.5 w-3.5" />
          {t.advancedFilters}
        </button>

        {tab === 'active' && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
              <input
                type="checkbox"
                checked={allActiveSelected}
                disabled={loading || ownedVisibleMeetings.length === 0}
                onChange={(event) => toggleAllActiveSelection(event.target.checked)}
                className="h-4 w-4 rounded border-[var(--border-color)] text-[var(--accent)]"
              />
              {t.selectAll}
            </label>
            <button
              type="button"
              onClick={() => setConfirmBulkActiveDelete(true)}
              disabled={selectedActiveIds.length === 0 || historyActionLoading}
              className="inline-flex h-9 items-center justify-center rounded-md bg-red-600 px-3 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.deleteSelected}
            </button>
          </div>
        )}

        {tab === 'deleted' && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
              <input
                type="checkbox"
                checked={allDeletedSelected}
                disabled={loading || meetings.length === 0}
                onChange={(event) => toggleAllDeletedSelection(event.target.checked)}
                className="h-4 w-4 rounded border-[var(--border-color)] text-[var(--accent)]"
              />
              {t.selectAll}
            </label>
            <button
              type="button"
              onClick={() => setConfirmBulkPermanentDelete(true)}
              disabled={selectedDeletedIds.length === 0 || trashActionLoading}
              className="inline-flex h-9 items-center justify-center rounded-md bg-red-600 px-3 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.deleteAllPermanently}
            </button>
          </div>
        )}

        <div
          className="mt-6 overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-sm"
          aria-busy={loading}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-[var(--border-color)] bg-[var(--bg-primary)]">
                <tr className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                  <th className="w-10 px-4 py-3 sm:px-5">
                    <span className="sr-only">{t.select}</span>
                  </th>
                  <th className="px-4 py-3 sm:px-5">{t.meetingNameCol}</th>
                  <th className="px-4 py-3 sm:px-5">
                    {tab === 'deleted' ? t.deletedDateCol : t.dateTimeCol}
                  </th>
                  {tab === 'deleted' ? (
                    <th className="px-4 py-3 sm:px-5">{t.remainingCol}</th>
                  ) : (
                    <>
                      <th className="px-4 py-3 sm:px-5">{t.durationCol}</th>
                      <th className="px-4 py-3 sm:px-5">{t.directionCol}</th>
                      <th className="px-4 py-3 sm:px-5">{t.sharedWithCol}</th>
                    </>
                  )}
                  <th className="px-4 py-3 sm:px-5">{t.actionCol}</th>
                </tr>
              </thead>
              <tbody>
                {loading && <HistoryLoadingRows mode={tab} />}

                {!loading && error && (
                  <tr>
                    <td
                      colSpan={tab === 'active' ? 7 : 5}
                      className="px-4 py-6 text-sm text-red-500 sm:px-5"
                    >
                      {error}
                    </td>
                  </tr>
                )}

                {!loading && !error && meetings.length === 0 && (
                  <tr>
                    <td
                      colSpan={tab === 'active' ? 7 : 5}
                      className="px-4 py-10 text-center text-sm text-[var(--text-secondary)] sm:px-5"
                    >
                      {tab === 'deleted' ? (
                        <TrashEmptyState label={t.emptyTrash} />
                      ) : (
                        t.noMeetingsFound
                      )}
                    </td>
                  </tr>
                )}

                {!loading &&
                  !error &&
                  meetings.map((meeting) => {
                    const accessState =
                      meeting.accessState ??
                      (meeting.ownership === 'shared' ? 'shared' : 'owner')
                    const isOwner = accessState === 'owner'
                    const isAccessRemoved = accessState === 'access_removed'
                    const isShared = accessState === 'shared'
                    const directCount =
                      meeting.directShareCount ?? meeting.sharedWithCount ?? 0
                    const shareableOk = meeting.shareable?.ok ?? meeting.hasMinutes
                    const shareDisabledReason =
                      meeting.shareable?.reason === 'processing'
                        ? t.shareDisabledProcessing
                        : meeting.shareable?.reason === 'error'
                          ? t.shareDisabledError
                          : t.shareDisabledNoContent

                    return (
                    <tr
                      key={meeting.id}
                      className={[
                        'border-b border-[var(--border-color)] last:border-b-0',
                        isAccessRemoved ? 'opacity-50' : '',
                      ].join(' ')}
                    >
                      <td className="px-4 py-4 sm:px-5">
                        {tab === 'active' ? (
                          isOwner ? (
                          <input
                            type="checkbox"
                            checked={selectedActiveIds.includes(meeting.id)}
                            onChange={() => toggleActiveSelection(meeting.id)}
                            className="h-4 w-4 rounded border-[var(--border-color)] text-[var(--accent)]"
                            aria-label={fmt(t.selectMeetingAria, { title: meeting.title })}
                          />
                          ) : null
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedDeletedIds.includes(meeting.id)}
                            onChange={() => toggleDeletedSelection(meeting.id)}
                            className="h-4 w-4 rounded border-[var(--border-color)] text-[var(--accent)]"
                            aria-label={fmt(t.selectMeetingAria, { title: meeting.title })}
                          />
                        )}
                      </td>
                      <td className="px-4 py-4 sm:px-5 max-w-80">
                        <div className="flex min-w-[220px] items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)]">
                            <MeetingGlyph />
                          </div>
                          <span className="min-w-0 break-words font-semibold text-[var(--text-primary)]">
                            {meeting.title}
                          </span>
                          {meeting.completed === false && (
                            <span className="inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                              {t.meetingUnfinishedBadge}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[var(--text-secondary)] sm:px-5">
                        {formatDateTime(
                          tab === 'deleted' && meeting.deletedAt
                            ? meeting.deletedAt
                            : meeting.createdAt,
                          locale
                        )}
                      </td>
                      {tab === 'deleted' ? (
                        <>
                          <td className="px-4 py-4 sm:px-5">
                            <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[var(--accent)]">
                              {formatDeletedRemainingDays(meeting.deletedAt, t)}
                            </span>
                          </td>
                          <td className="px-4 py-4 sm:px-5">
                            <div className="flex flex-wrap items-center gap-3 text-xs font-semibold">
                              <button
                                type="button"
                                disabled={trashActionLoading}
                                className="text-[var(--accent)] disabled:opacity-50"
                                onClick={() => void handleRestoreMeeting(meeting)}
                              >
                                {t.restore}
                              </button>
                              <button
                                type="button"
                                disabled={trashActionLoading}
                                className="text-red-500 disabled:opacity-50"
                                onClick={() => setMeetingToPermanentlyDelete(meeting)}
                              >
                                {t.permanentDelete}
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-4 text-[var(--text-secondary)] sm:px-5">
                            {formatDuration(meeting.durationMinutes, t)}
                          </td>
                          <td className="px-4 py-4 sm:px-5">
                            <span className="inline-flex items-center gap-1.5 text-xs text-[var(--text-tertiary)]">
                              <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-bold text-white">
                                {getLanguageLabel(meeting.sourceLang)}
                              </span>
                              <span aria-hidden="true">→</span>
                              <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-bold text-white">
                                {getLanguageLabel(meeting.targetLang)}
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-4 sm:px-5">
                            {isShared || isAccessRemoved ? (
                              <span className="inline-flex items-center gap-2 text-sm text-[var(--text-primary)]">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-[10px] font-bold text-[var(--accent)]">
                                  {(meeting.sharedBy?.name ?? '?').slice(0, 1).toUpperCase()}
                                </span>
                                <span className="min-w-0 truncate">
                                  {meeting.sharedBy?.name ?? '—'}
                                </span>
                              </span>
                            ) : directCount > 0 ? (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)]"
                                onClick={() => setMeetingToShare(meeting)}
                              >
                                <UsersIcon className="h-4 w-4" />
                                {directCount === 1
                                  ? t.onePerson
                                  : fmt(t.nPeople, { count: directCount })}
                              </button>
                            ) : (
                              <span className="text-sm text-[var(--text-tertiary)]">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 sm:px-5">
                            {isAccessRemoved ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--text-tertiary)]">
                                <span aria-hidden>🔒</span>
                                {t.accessRemoved}
                              </span>
                            ) : (
                            <div className="flex flex-wrap items-center gap-3 text-xs font-semibold">
                              <Link
                                to={`/meetings/${meeting.id}`}
                                state={{ from: '/history' }}
                                className="text-[var(--accent)]"
                              >
                                {t.view}
                              </Link>
                              <button
                                type="button"
                                className="text-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={!shareableOk}
                                title={!shareableOk ? shareDisabledReason : undefined}
                                onClick={() => setMeetingToShare(meeting)}
                              >
                                {t.share}
                              </button>
                              {isOwner && (
                                  <button
                                    type="button"
                                    className="text-red-500"
                                    onClick={() => setMeetingToDelete(meeting)}
                                  >
                                    {t.delete}
                                  </button>
                              )}
                              {isShared && (
                                <button
                                  type="button"
                                  className="text-red-500"
                                  onClick={() => setMeetingToRemove(meeting)}
                                >
                                  {t.removeFromHistory}
                                </button>
                              )}
                            </div>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-color)] px-4 py-3 text-xs text-[var(--text-secondary)] sm:px-5">
            {loading ? (
              <>
                <div className="h-4 w-40 animate-pulse rounded bg-[var(--bg-tertiary)]" />
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className="h-8 w-8 animate-pulse rounded-md bg-[var(--bg-tertiary)]"
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <p>
                  {total === 0
                    ? t.showingRangeEmpty
                    : fmt(t.showingRange, { start: rangeStart, end: rangeEnd, total })}
                </p>
                <div className="flex items-center gap-1">
                  <PaginationButton
                    label={t.previousPage}
                    disabled={currentPage <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    ‹
                  </PaginationButton>
                  {getPageButtonWindow(currentPage, totalPages, MAX_PAGE_BUTTONS).map(
                    (pageNumber) => (
                      <PaginationButton
                        key={pageNumber}
                        label={fmt(t.pageN, { n: pageNumber })}
                        active={pageNumber === currentPage}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </PaginationButton>
                    )
                  )}
                  <PaginationButton
                    label={t.nextPage}
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                  >
                    ›
                  </PaginationButton>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {meetingToDelete && (
        <DeleteMeetingModal
          title={t.deleteMeetingTitle}
          body={fmt(t.deleteMeetingBody, { title: meetingToDelete.title })}
          deleting={deleting}
          onClose={() => {
            if (!deleting) setMeetingToDelete(null)
          }}
          onConfirm={() => void handleConfirmDelete()}
        />
      )}

      {meetingToShare ? (
        <ShareMeetingFlow
          meetingId={meetingToShare.id}
          meetingTitle={meetingToShare.title}
          onClose={() => setMeetingToShare(null)}
          onShared={(shared, meta) => {
            const delta = meta?.newDirectShares ?? shared.length
            if (delta === 0) return
            setMeetings((current) =>
              current.map((m) =>
                m.id === meetingToShare.id
                  ? {
                      ...m,
                      sharedWithCount: (m.sharedWithCount ?? 0) + delta,
                      directShareCount:
                        (m.directShareCount ?? m.sharedWithCount ?? 0) + delta,
                    }
                  : m
              )
            )
          }}
          onRevoked={() => {
            setMeetings((current) =>
              current.map((m) =>
                m.id === meetingToShare.id
                  ? {
                      ...m,
                      directShareCount: Math.max(
                        0,
                        (m.directShareCount ?? m.sharedWithCount ?? 1) - 1,
                      ),
                      sharedWithCount: Math.max(0, (m.sharedWithCount ?? 1) - 1),
                    }
                  : m
              )
            )
          }}
        />
      ) : null}

      {meetingToRemove && (
        <RemoveMeetingModal
          removing={removing}
          onClose={() => {
            if (!removing) setMeetingToRemove(null)
          }}
          onConfirm={() => void handleRemoveFromHistory()}
        />
      )}

      {confirmBulkActiveDelete && (
        <DeleteMeetingModal
          title={fmt(t.deleteSelectedMeetingsTitle, { count: selectedActiveIds.length })}
          body={fmt(t.deleteSelectedMeetingsBody, { count: selectedActiveIds.length })}
          confirmLabel={t.deleteSelected}
          deleting={historyActionLoading}
          onClose={() => {
            if (!historyActionLoading) setConfirmBulkActiveDelete(false)
          }}
          onConfirm={() => void handleDeleteSelectedActiveMeetings()}
        />
      )}

      {meetingToPermanentlyDelete && (
        <DeleteMeetingModal
          title={t.permanentDeleteMeetingTitle}
          body={fmt(t.permanentDeleteMeetingBody, { title: meetingToPermanentlyDelete.title })}
          confirmLabel={t.permanentDelete}
          deleting={trashActionLoading}
          onClose={() => {
            if (!trashActionLoading) setMeetingToPermanentlyDelete(null)
          }}
          onConfirm={() => void handlePermanentDeleteMeeting(meetingToPermanentlyDelete)}
        />
      )}

      {confirmBulkPermanentDelete && (
        <DeleteMeetingModal
          title={fmt(t.permanentDeleteSelectedMeetingsTitle, { count: selectedDeletedIds.length })}
          body={fmt(t.permanentDeleteSelectedMeetingsBody, { count: selectedDeletedIds.length })}
          confirmLabel={t.deleteAllPermanently}
          deleting={trashActionLoading}
          onClose={() => {
            if (!trashActionLoading) setConfirmBulkPermanentDelete(false)
          }}
          onConfirm={() => void handlePermanentDeleteSelected()}
        />
      )}
    </>
  )
}

function HistoryLoadingRows({ mode }: { mode: HistoryTab }) {
  const colSpan = mode === 'active' ? 7 : 5

  return (
    <>
      {Array.from({ length: 7 }, (_, index) => (
        <tr key={index} className="border-b border-[var(--border-color)]">
          <td colSpan={colSpan} className="px-4 py-4 sm:px-5">
            <div className="grid animate-pulse items-center gap-4 md:grid-cols-[minmax(220px,1.4fr)_minmax(130px,1fr)_90px_100px_100px_auto]">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 shrink-0 rounded-md bg-[var(--bg-tertiary)]" />
                <div className="h-4 w-44 rounded bg-[var(--bg-tertiary)]" />
              </div>
              <div className="h-4 w-36 rounded bg-[var(--bg-tertiary)]" />
              <div className="h-4 w-16 rounded bg-[var(--bg-tertiary)]" />
              <div className="h-5 w-20 rounded bg-[var(--bg-tertiary)]" />
              <div className="h-4 w-20 rounded bg-[var(--bg-tertiary)]" />
              {mode === 'active' ? (
                <div className="h-4 w-24 rounded bg-[var(--bg-tertiary)]" />
              ) : null}
            </div>
          </td>
        </tr>
      ))}
    </>
  )
}

function TrashEmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg-tertiary)] text-[var(--text-tertiary)]">
        <TrashIcon className="h-6 w-6" />
      </div>
      <p className="font-semibold text-[var(--text-secondary)]">{label}</p>
    </div>
  )
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M7 12h10M10 17h4" />
    </svg>
  )
}
